import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Check, Hourglass, Landmark } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * DATOS DEL MORTGAGE.
 *
 * Pedido de Payments (2026-08-04). Para endosar un cheque hay que llamar al banco, y en esa llamada
 * piden el NÚMERO DE PRÉSTAMO — que hasta ahora vivía en la cabeza de alguien o en un update
 * suelto. Lo mismo la dirección postal a la que se manda el cheque.
 *
 * La fecha de envío es la que más pesa: sin ella, "¿ya lo mandaste?" no tiene respuesta y el cheque
 * se queda semanas dando vueltas sin que nadie lo note. Por eso arriba se muestra el estado en una
 * línea: enviado el día tal, o todavía sin enviar.
 */

type Claim = {
  mortgage_company?: string | null; mortgage_name?: string | null;
  mortgage_loan_number?: string | null; mortgage_po_box?: string | null;
  mortgage_check_sent_on?: string | null;
};

const CAMPOS = [
  ['mortgage_company', 'mtg.company'],
  ['mortgage_name', 'mtg.name'],
  ['mortgage_loan_number', 'mtg.loan'],
  ['mortgage_po_box', 'mtg.poBox'],
] as const;

export function MortgageBox({ claimId, claim, onSaved }: {
  claimId: string; claim: Claim; onSaved?: () => void;
}) {
  const { t } = useI18n();
  const [f, setF] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    setF({
      mortgage_company: claim.mortgage_company ?? '',
      mortgage_name: claim.mortgage_name ?? '',
      mortgage_loan_number: claim.mortgage_loan_number ?? '',
      mortgage_po_box: claim.mortgage_po_box ?? '',
      mortgage_check_sent_on: (claim.mortgage_check_sent_on ?? '').slice(0, 10),
    });
  }, [claim]);

  async function guardar() {
    setBusy(true); setOk(false);
    try {
      await api.patch(`/api/claims/${claimId}/info`, f);
      setOk(true); onSaved?.();
      setTimeout(() => setOk(false), 2500);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  const enviado = (f.mortgage_check_sent_on ?? '').length === 10;

  return (
    <div className="card" style={{ display: 'grid', gap: 9 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ margin: 0 }}><Landmark size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('mtg.title')}</h2>
        {/* El estado del cheque, arriba y en una línea: es lo primero que se pregunta. */}
        <span className="badge" style={{
          fontSize: 'var(--fs-2xs)',
          background: enviado ? 'rgba(22,163,74,.14)' : 'rgba(180,83,9,.14)',
          color: enviado ? 'var(--green)' : 'var(--orange)',
        }}>
          {enviado
            ? <><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('mtg.sentOn', { d: new Date(`${f.mortgage_check_sent_on}T12:00:00Z`).toLocaleDateString(localeUI()) })}</>
            : <><Hourglass size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('mtg.notSent')}</>}
        </span>
        {ok && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', color: 'var(--green)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('common.saved')}</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
        {CAMPOS.map(([k, label]) => (
          <label key={k} style={{ display: 'grid', gap: 3 }}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t(label)}</span>
            <input className="input sm" value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </label>
        ))}
        <label style={{ display: 'grid', gap: 3 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('mtg.checkSent')}</span>
          <input className="input sm" type="date" value={f.mortgage_check_sent_on ?? ''}
            onChange={(e) => setF({ ...f, mortgage_check_sent_on: e.target.value })} />
        </label>
      </div>

      <div>
        <button className="btn sm" disabled={busy} onClick={() => void guardar()}>{t('common.save')}</button>
      </div>
    </div>
  );
}
